import {
  computed,
  effect,
  inject,
  Injectable,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { queryParam } from '@softline/application';
import { base64Decode, DateService, isDefinedNotEmpty, IsoDate } from '@softline/core';
import { DateRange } from '@softline/ui-core';
import { QueryFilterModel, SalesStatisticsQuery } from '../types/sales-statistics-query';
import { SalesStatisticsTemplate } from '../types/sales-statistics-template';
import { StatisticTypeCode } from '../types/statistic-type';

export interface ActiveFilter {
  type: string;
  name?: string;
  value?: string;
  values?: string[];
  operator?: 'AND' | 'OR';
  filters?: ActiveFilter[];
}

export interface ActiveFilterEntry {
  field: string;
  filter: ActiveFilter;
}

export const FILTER_MATCH_MODES = ['OR', 'CURRENT', 'PREVIOUS'] as const;
export type FilterMatchMode = (typeof FILTER_MATCH_MODES)[number];

export const COMPOSITE_SEP = '\u001f';

function toIsoDateString(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const DRILL_LEVELS_STORAGE_KEY = 'soft-sales-statistics-drill-levels';

function readStoredDrillLevels(): Record<string, string> {
  try {
    const raw = localStorage.getItem(DRILL_LEVELS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

@Injectable()
export class SalesStatisticsQueryService {
  readonly #queryParams = queryParam('data');

  private dateService = inject(DateService);

  readonly dateRange = linkedSignal(() => {
    const queryData = this.#queryParamWithFilterYear();

    if (isDefinedNotEmpty(queryData?.primary?.filterModel?.['year']?.['filter']))
      return {
        from: `${queryData?.primary?.filterModel?.['year']?.['filter']}-01-01`,
        to: `${queryData?.primary?.filterModel?.['year']?.['filter']}-12-31`,
      } as DateRange;

    const today = new Date();
    const year = today.getFullYear();
    return { from: `${year}-01-01`, to: toIsoDateString(today) } as DateRange;
  });

  readonly referencePeriod = linkedSignal<DateRange | null>(() => {
    const queryData = this.#queryParamWithFilterYear();

    if (isDefinedNotEmpty(queryData?.comparison?.filterModel?.['year']?.['filter'])) {
      return {
        from: `${queryData?.comparison?.filterModel?.['year']?.['filter']}-01-01`,
        to: `${queryData?.comparison?.filterModel?.['year']?.['filter']}-12-31`,
      } as DateRange;
    } else if (isDefinedNotEmpty(queryData?.primary?.filterModel?.['year']?.['filter'])) {
      return {
        from: `${queryData?.primary?.filterModel?.['year']?.['filter']}-01-01`,
        to: `${queryData?.primary?.filterModel?.['year']?.['filter']}-12-31`,
      } as DateRange;
    }
    const today = new Date();
    const lastYear = today.getFullYear() - 1;
    const lastYearToday = new Date(today);
    lastYearToday.setFullYear(lastYear);
    return {
      from: `${lastYear}-01-01`,
      to: toIsoDateString(lastYearToday),
    } as DateRange;
  });

  readonly salesOrganisation = signal<string[] | null>(null);
  readonly productGroup = signal<string[] | null>(null);
  readonly mainGroup = signal<string[] | null>(null);
  readonly documentType = signal<string>('INVOICE');
  readonly statisticType = signal<StatisticTypeCode>('TOTAL');
  readonly distributionChannel = signal<string[] | null>(null);
  readonly subGroup = signal<string[] | null>(null);
  readonly subSubGroup = signal<string[] | null>(null);
  readonly product = signal<string[] | null>(null);
  readonly warehouse = signal<string[] | null>(null);
  readonly customerGroup = signal<string[] | null>(null);
  readonly customer = signal<string[] | null>(null);
  readonly salesRepresentative = signal<string[] | null>(null);
  readonly oldStatisticType = signal<string[] | null>(null);
  readonly conditionGroup = signal<string[] | null>(null);
  readonly vehicle = signal<string[] | null>(null);
  readonly vehicleGroup = signal<string[] | null>(null);

  readonly inventoryEnabled = signal<boolean>(false);

  readonly inventoryDataTick = signal(0);

  private readonly bumpInventoryDataTick = effect(() => {
    const enabled = this.inventoryEnabled();
    if (!enabled) return;
    untracked(() => this.inventoryDataTick.update((n) => n + 1));
  });

  readonly filterMatchMode = signal<FilterMatchMode>('OR');

  readonly selectedMetric = signal<
    'revenue' | 'db' | 'dbPercent' | 'inventoryValue' | null
  >('revenue');

  readonly fromChatbot = signal(false);

  readonly chatbotApplyTick = signal(0);

  notifyChatbotApply(): void {
    this.fromChatbot.set(true);
    this.chatbotApplyTick.update((n) => n + 1);
  }

  readonly debugLogging = signal(false);

  readonly refreshTick = signal(0);

  refresh(): void {
    this.refreshTick.update((n) => n + 1);
  }

  readonly drillLevels = signal<Record<string, string>>(readStoredDrillLevels());

  private readonly persistDrillLevels = effect(() => {
    const levels = this.drillLevels();
    try {
      localStorage.setItem(DRILL_LEVELS_STORAGE_KEY, JSON.stringify(levels));
    } catch {
      // Storage unavailable (e.g. private mode) — levels just aren't persisted.
    }
  });

  readonly activeFilterEntries = computed<ActiveFilterEntry[]>(() => {
    const entries: ActiveFilterEntry[] = [];

    const multiFilters: [string, string[] | null][] = [
      ['sales_organisation.sales_organisation', this.salesOrganisation()],
      ['product_group.code', this.productGroup()],
      ['warehouse.warehouse', this.warehouse()],
      ['distribution_channel.distribution_channel', this.distributionChannel()],
      ['product.number', this.product()],
      ['sales_representative.sales_representative_number', this.salesRepresentative()],
      ['statistic_type.statistic_type_code', this.oldStatisticType()],
      ['condition_group.condition_group', this.conditionGroup()],
      ['vehicle.vehicle', this.vehicle()],
      ['vehicle_group.vehicle_group', this.vehicleGroup()],
    ];
    for (const [field, values] of multiFilters) {
      if (values && values.length > 0) {
        const filter: ActiveFilter = values.length === 1
          ? { type: 'stringEquals', name: field, value: values[0] }
          : { type: 'stringSet', name: field, values };
        entries.push({ field, filter });
      }
    }

    const compositeFilters: [string, string, string[] | null][] = [
      ['product_main_group.code', 'product_main_group_language.name', this.mainGroup()],
      ['product_sub_group.code', 'product_sub_group_language.name', this.subGroup()],
      ['product_sub_sub_group.code', 'product_sub_sub_group_language.name', this.subSubGroup()],
      ['customer_group.customer_group', 'customer_group_language.name', this.customerGroup()],
    ];
    for (const [codeField, nameField, values] of compositeFilters) {
      if (!values || values.length === 0) continue;
      const andGroups = values.map((v) => {
        const idx = v.indexOf(COMPOSITE_SEP);
        const code = idx >= 0 ? v.substring(0, idx) : v;
        const label = idx >= 0 ? v.substring(idx + COMPOSITE_SEP.length) : '';
        return {
          type: 'conditions',
          operator: 'AND',
          filters: [
            { type: 'stringEquals', name: codeField, value: code },
            { type: 'stringEquals', name: nameField, value: label },
          ],
        } as ActiveFilter;
      });
      entries.push({
        field: codeField,
        filter: { type: 'conditions', operator: 'OR', filters: andGroups },
      });
    }

    const customerValues = this.customer();
    if (customerValues && customerValues.length > 0) {
      const andGroups = customerValues.map((v) => {
        const [num, sub] = v.split(',');
        return {
          type: 'conditions',
          operator: 'AND',
          filters: [
            { type: 'stringEquals', name: 'customer.number', value: num },
            { type: 'stringEquals', name: 'customer.sub_number', value: sub ?? '0' },
          ],
        } as ActiveFilter;
      });
      entries.push({
        field: 'customer.number',
        filter: { type: 'conditions', operator: 'OR', filters: andGroups },
      });
    }

    return entries;
  });

  readonly activeFilters = computed<ActiveFilter[]>(() =>
    this.activeFilterEntries().map((e) => e.filter)
  );

  readonly #queryParamWithFilterYear = computed(() => {
    const query = this.#queryParams();

    if (!query) return null;

    return JSON.parse(base64Decode(query)) as SalesStatisticsQuery;
  });

  readonly queryParam = computed(() => {
    let queryParamWithFilterYear = this.#queryParamWithFilterYear();

    if (!queryParamWithFilterYear) return null;

    let primaryFilterModel = {} as QueryFilterModel;

    for (const [key, value] of Object.entries(
      queryParamWithFilterYear.primary.filterModel
    )) {
      if (key !== 'year') primaryFilterModel = { ...primaryFilterModel, [key]: value };
    }

    let comparisonFilterModel = {} as QueryFilterModel;

    if (queryParamWithFilterYear?.comparison) {
      for (const [key, value] of Object.entries(
        queryParamWithFilterYear.comparison.filterModel
      )) {
        if (key !== 'year')
          comparisonFilterModel = { ...comparisonFilterModel, [key]: value };
      }
    }

    queryParamWithFilterYear = {
      primary: {
        ...queryParamWithFilterYear.primary,
        filterModel: primaryFilterModel,
      },
      comparison: queryParamWithFilterYear.comparison
        ? {
            ...queryParamWithFilterYear.comparison,
            filterModel: comparisonFilterModel,
          }
        : null,
    };

    return queryParamWithFilterYear as SalesStatisticsQuery;
  });

  getTemplateValue(): SalesStatisticsTemplate {
    return {
      dateRange: this.dateRange(),
      referencePeriod: this.referencePeriod(),
      salesOrganisation: this.salesOrganisation(),
      productGroup: this.productGroup(),
      mainGroup: this.mainGroup(),
      distributionChannel: this.distributionChannel(),
      subGroup: this.subGroup(),
      subSubGroup: this.subSubGroup(),
      product: this.product(),
      warehouse: this.warehouse(),
      customerGroup: this.customerGroup(),
      customer: this.customer(),
      salesRepresentative: this.salesRepresentative(),
      oldStatisticType: this.oldStatisticType(),
      conditionGroup: this.conditionGroup(),
      vehicle: this.vehicle(),
      vehicleGroup: this.vehicleGroup(),
      documentType: this.documentType(),
      statisticType: this.statisticType(),
      drillLevels: this.drillLevels(),
    };
  }

  applyTemplate(v: Partial<SalesStatisticsTemplate>): void {
    if (isDefinedNotEmpty(v.dateRange)) {
      this.dateRange.set({
        from: v.dateRange.from as IsoDate,
        to: v.dateRange.to as IsoDate,
      });
    }
    if (v.referencePeriod) this.referencePeriod.set(v.referencePeriod);
    this.salesOrganisation.set(v.salesOrganisation ? [v.salesOrganisation].flat() : null);
    this.productGroup.set(v.productGroup ? [v.productGroup].flat() : null);
    this.mainGroup.set(v.mainGroup ? [v.mainGroup].flat() : null);
    this.distributionChannel.set(
      v.distributionChannel ? [v.distributionChannel].flat() : null
    );
    this.subGroup.set(v.subGroup ? [v.subGroup].flat() : null);
    this.subSubGroup.set(v.subSubGroup ? [v.subSubGroup].flat() : null);
    this.product.set(v.product ? [v.product].flat() : null);
    this.warehouse.set(v.warehouse ? [v.warehouse].flat() : null);
    this.customerGroup.set(v.customerGroup ? [v.customerGroup].flat() : null);
    this.customer.set(v.customer ? [v.customer].flat() : null);
    this.salesRepresentative.set(
      v.salesRepresentative ? [v.salesRepresentative].flat() : null
    );
    this.oldStatisticType.set(v.oldStatisticType ? [v.oldStatisticType].flat() : null);
    this.conditionGroup.set(v.conditionGroup ? [v.conditionGroup].flat() : null);
    this.vehicle.set(v.vehicle ? [v.vehicle].flat() : null);
    this.vehicleGroup.set(v.vehicleGroup ? [v.vehicleGroup].flat() : null);
    if (isDefinedNotEmpty(v.documentType)) this.documentType.set(v.documentType);
    if (isDefinedNotEmpty(v.statisticType)) this.statisticType.set(v.statisticType);
    this.drillLevels.set(v.drillLevels ? { ...v.drillLevels } : {});
  }
}
